import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Stack,
} from "@mui/material";
import { Table, Tag } from "antd";
import {
  listProviderApplications,
  markProviderFormReceived,
  rejectProviderApplication,
  verifyProviderApplication,
} from "../services/providerApplicationService";
import {
  PageHeader,
} from "../../../shared/components/pageShell";


const STATUS_COLOR = {
  intake_submitted: "blue",
  form_pending: "gold",
  in_review: "purple",
  verified: "green",
  rejected: "red",
};

export default function ProviderApplicationsQueue() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const result = await listProviderApplications();
    if (!result.success) {
      setError(result.error || "Failed to load applications");
      setRows([]);
    } else {
      setRows(result.data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const runAction = async (fn) => {
    const result = await fn();
    if (!result?.ok && !result?.success) {
      setError(result?.code || result?.error || "Action failed");
      return;
    }
    await load();
  };

  const columns = [
    { title: "Name", dataIndex: "fullName", key: "fullName" },
    { title: "Email", dataIndex: "email", key: "email" },
    { title: "Phone", dataIndex: "phone", key: "phone" },
    { title: "City", dataIndex: "city", key: "city" },
    {
      title: "Owner",
      dataIndex: "isOwner",
      key: "isOwner",
      render: (value) => (value ? "Yes" : "No"),
    },
    {
      title: "Coverage",
      dataIndex: "coveragePreference",
      key: "coveragePreference",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={STATUS_COLOR[status] || "default"}>{status}</Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={() => runAction(() => markProviderFormReceived(record.id))}
            disabled={record.status === "verified" || record.status === "rejected"}
          >
            Form received
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={() => runAction(() => verifyProviderApplication(record.id))}
            disabled={record.status === "verified" || record.status === "rejected"}
          >
            Verify
          </Button>
          <Button
            size="small"
            color="error"
            onClick={() => runAction(() => rejectProviderApplication(record.id))}
            disabled={record.status === "rejected"}
          >
            Reject
          </Button>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ mb: 3 }}>
      <PageHeader
        title="Provider applications"
        subtitle="Manual vetting queue. Partner records are created only on Verify."
        actions={<Button onClick={load}>Refresh</Button>}
      />
      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table
            columns={columns}
            dataSource={rows}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 10 }}
            scroll={{ x: "max-content" }}
          />
        </Box>
      )}
    </Box>
  );
}
